import React from "react";

import { LuChevronLeft } from "react-icons/lu";
import { styled } from "styled-components";

const Page = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f6f6f6;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TitleBar = styled.div`
  width: 90%;
  display: flex;
  align-items: center;
  margin-bottom: 45px;
`;

const BackButton = styled(LuChevronLeft)`
  cursor: pointer;
  color: #333333;
`;

const Header = styled.h1`
  color: #000;
  font-feature-settings: "clig" off, "liga" off;
  font-family: "NotoSansKRBold";
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: 44px;
  letter-spacing: -1.8px;
  margin-top: 10px;
`;

const Board = styled.div`
  background-color: white;
  width: 90%;
  height: 100%;
  margin-bottom: 50px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 24px;
`;

const ContentColumn = styled.div`
  margin-right: 15px;
`;

const BoardTitle = styled.div`
  color: var(--text-black, #111);
  font-family: "NotoSansKRBold";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  margin-top: 15px;
`;

const User = styled.div`
  color: var(--text-black, #111);
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  opacity: 0.5;
  margin-top: 6px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 28px;
`;

const MetaText = styled.div`
  color: rgba(0, 0, 0, 0.7);
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const PostedAt = styled(MetaText)`
  margin-left: 30px;
`;

const ViewCount = styled(MetaText)`
  margin-right: 30px;
`;

const BodyContent = styled.div`
  margin-right: 30px;
  margin-left: 30px;
  color: #000;
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const ContentImage = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 50px;
  margin-left: 30px;
`;

const AnswerTextarea = styled.textarea`
  margin-right: 30px;
  width: 100%;
  height: 180px;
  padding-left: 15px;
  padding-top: 15px;
  font-family: "Noto Sans KR";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: -0.56px;
  border-radius: 5px;
  border: 2px solid var(--admin-gray-stroke, #e8e8e8);

  &:focus {
    outline: none;
  }

  &::placeholder {
    opacity: 0.5;
  }
`;

const Warning = styled.div`
  padding-left: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 30px;
  background-color: #f6f6f6;
  width: 95%;
  height: 64px;
`;

const WarningList = styled.li`
  color: var(--text-gray, #5c5c5c);
  margin-left: 10px;
  font-family: "Noto Sans KR";
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 200px;
`;

const SecondaryButton = styled.button`
  display: flex;
  width: 120px;
  height: 35px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 5px;
  border: 1px solid var(--admin-gray-stroke, #e8e8e8);
  background: var(--white, #fff);
  margin-right: 20px;
  color: #000;
  text-align: center;
  font-family: "Noto Sans KR";
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const PrimaryButton = styled(SecondaryButton)`
  background: ${(props) => (props.disabled ? "#cfd4d9" : "#0079e0")};
  color: #fff;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

function CommunicationAnswerDetail({
  title,
  boardTitle,
  userText,
  postedAt,
  viewCount,
  statusBadge,
  content,
  imageSrc,
  answer,
  onAnswerChange,
  warningMessages,
  deleteLabel,
  onDelete,
  progressLabel,
  onProgress,
  showProgressButton,
  answerButtonLabel,
  onAnswerSubmit,
  onBack,
}) {
  // 답변 내용이 비어 있으면(공백만 포함) 답변하기 버튼 비활성화
  const isAnswerEmpty = !answer || answer.trim() === "";

  return (
    <Page>
      <TitleBar>
        <BackButton size={32} onClick={onBack} />
        <Header>{title}</Header>
      </TitleBar>
      <Board>
        <ContentContainer>
          <ContentColumn>{statusBadge}</ContentColumn>
          <ContentColumn>
            <BoardTitle>{boardTitle}</BoardTitle>
            {userText ? <User>{userText}</User> : null}
          </ContentColumn>
        </ContentContainer>
        <MetaRow>
          <PostedAt>
            게시일시 {postedAt}
            {postedAt && postedAt !== "-" ? " (KST)" : ""}
          </PostedAt>
          <ViewCount>조회 수 {viewCount}</ViewCount>
        </MetaRow>
        <BodyContent
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
        {imageSrc ? <ContentImage src={imageSrc} alt="" /> : null}
        <BodyContent>
          <AnswerTextarea
            value={answer}
            placeholder="답변을 입력해주세요"
            onChange={(event) => onAnswerChange(event.target.value)}
          />
        </BodyContent>
        <Warning>
          {warningMessages.map((message) => (
            <WarningList key={message}>{message}</WarningList>
          ))}
        </Warning>
      </Board>
      <ButtonContainer>
        <SecondaryButton onClick={onDelete}>{deleteLabel}</SecondaryButton>
        {showProgressButton ? (
          <SecondaryButton onClick={onProgress}>{progressLabel}</SecondaryButton>
        ) : null}
        <PrimaryButton onClick={onAnswerSubmit} disabled={isAnswerEmpty}>
          {answerButtonLabel}
        </PrimaryButton>
      </ButtonContainer>
    </Page>
  );
}

export default CommunicationAnswerDetail;
